import type { Lesson } from "@/core/types";
import { criterion, fail, fromCriteria } from "@/core/check";
import { hatVerbundeneZellen } from "@/office/word/einfuegen";
import { readTableDetails, readTables } from "@/office/word/tables";

export const lektionTabellenUmgang: Lesson = {
  id: "umgang",
  title: "Spaltenbreite, Zellen verbinden und Sortieren",
  summary: "Die Handgriffe, die im Alltag am häufigsten gebraucht werden.",
  durationMinutes: 8,
  learningGoals: [
    "Spaltenbreiten sinnvoll anpassen",
    "Zellen verbinden und teilen",
    "Tabellen nach einer Spalte sortieren",
  ],
  steps: [
    {
      id: "spaltenbreite",
      title: "Spaltenbreite anpassen",
      explanation: [
        "Spaltenbreiten lassen sich mit der Maus an der Trennlinie ziehen. Das ist schnell, " +
          "führt aber leicht zu krummen Werten und ungleichen Spalten.",
        "Sauberer geht es über die Registerkarte „Layout“: „Spalten gleichmäßig verteilen“ " +
          "macht alle Spalten gleich breit, „AutoAnpassen“ → „Inhalt“ passt jede Spalte an " +
          "ihren längsten Eintrag an, und „AutoAnpassen“ → „Fenster“ zieht die Tabelle auf " +
          "die volle Textbreite.",
        "Ein Tipp für den Alltag: Hältst du beim Ziehen der Trennlinie Alt gedrückt, zeigt " +
          "Word die genauen Maße im Lineal an.",
      ],
      keyPoints: [
        "Layout → AutoAnpassen → Inhalt passt die Spalten an die Einträge an.",
        "Layout → Spalten gleichmäßig verteilen macht sie gleich breit.",
        "Doppelklick auf die Trennlinie passt eine einzelne Spalte an.",
      ],
      task: {
        instruction:
          "Lege eine Tabelle mit mindestens drei Spalten an und passe die Spaltenbreiten an " +
          "den Inhalt an, sodass die Spalten unterschiedlich breit sind.",
        hint: "Cursor in die Tabelle, Layout → AutoAnpassen → Inhalt automatisch anpassen.",
        check: async () => {
          const details = await readTableDetails();
          const angepasst = details.filter((tabelle) => {
            const ersteZeile = tabelle.cellWidths[0] ?? [];
            if (ersteZeile.length < 3) return false;
            return Math.max(...ersteZeile) - Math.min(...ersteZeile) > 5;
          });
          if (details.length === 0) {
            return fail("Im Dokument gibt es noch keine Tabelle.", "Einfügen → Tabelle.");
          }
          return fromCriteria(
            [criterion("Eine Tabelle mit mindestens drei unterschiedlich breiten Spalten", angepasst.length > 0)],
            {
              success: "Die Spalten richten sich jetzt nach ihrem Inhalt.",
              failure: "Alle Spalten sind noch gleich breit.",
              hint: "AutoAnpassen → Inhalt wirkt nur, wenn die Einträge unterschiedlich lang sind.",
            },
          );
        },
      },
    },
    {
      id: "zellen-verbinden",
      title: "Zellen verbinden und teilen",
      explanation: [
        "Manchmal soll eine Überschrift über mehrere Spalten laufen. Dafür markierst du " +
          "die betreffenden Zellen und wählst unter „Layout“ → „Zellen verbinden“. " +
          "„Zellen teilen“ macht es rückgängig und fragt nach der gewünschten Aufteilung.",
        "Setze das sparsam ein. Verbundene Zellen machen eine Tabelle für Screenreader " +
          "schwer lesbar, weil die Zuordnung von Zelle zu Spaltenüberschrift verloren " +
          "geht. Auch Sortieren funktioniert dann nicht mehr zuverlässig.",
        "Wenn du merkst, dass du viele Zellen verbinden musst, sind es meistens zwei " +
          "Tabellen statt einer – oder du bist wieder beim Layouten mit Tabellen aus der " +
          "zweiten Lektion.",
      ],
      keyPoints: [
        "Layout → Zellen verbinden bzw. Zellen teilen.",
        "Verbundene Zellen erschweren Screenreadern die Zuordnung.",
        "Viele Verbindungen sind ein Zeichen für die falsche Struktur.",
      ],
      task: {
        instruction:
          "Verbinde in einer Tabelle zwei nebeneinanderliegende Zellen, etwa für eine " +
          "Überschrift, die über zwei Spalten läuft.",
        hint: "Beide Zellen markieren, Layout → Zellen verbinden.",
        check: async () => {
          const details = await readTableDetails();
          const verbunden = details.filter((tabelle) => hatVerbundeneZellen(tabelle.ooxml));
          return fromCriteria([criterion("Eine Tabelle enthält verbundene Zellen", verbunden.length > 0)], {
            success: "Verbunden – und du weißt, warum man das sparsam einsetzt.",
            failure: "Es gibt noch keine verbundenen Zellen.",
            hint: "Eine Zelle ohne Rahmenlinie sieht nur verbunden aus – nutze Layout → Zellen verbinden.",
          });
        },
      },
    },
    {
      id: "sortieren",
      title: "Sortieren",
      explanation: [
        "Word kann Tabellen sortieren, ohne dass man Zeilen von Hand verschiebt: " +
          "Cursor in die Tabelle, dann „Layout“ → „Sortieren“. Im Dialog wählst du die " +
          "Spalte, den Typ (Text, Zahl, Datum) und die Richtung.",
        "Achte auf die Auswahl „Überschriftszeile“ unten im Dialog. Ist sie gesetzt, " +
          "bleibt die erste Zeile stehen und wird nicht mit einsortiert – ein weiterer " +
          "Grund, die Überschriftenzeile richtig zu markieren.",
        "Und der wichtigste Hinweis: Der Typ muss stimmen. Sortiert Word Zahlen als Text, " +
          "kommt 10 vor 9, weil die 1 vor der 9 steht. Bei Datumsangaben gilt dasselbe.",
      ],
      keyPoints: [
        "Layout → Sortieren, mit Spalte, Typ und Richtung.",
        "„Überschriftszeile“ im Dialog schützt die Kopfzeile.",
        "Falscher Typ sortiert 10 vor 9 – immer Text, Zahl oder Datum passend wählen.",
      ],
      task: {
        instruction:
          "Sortiere eine Tabelle mit Überschriftenzeile und mindestens drei Datenzeilen " +
          "aufsteigend nach der ersten Spalte.",
        hint: "Cursor in die Tabelle, Layout → Sortieren, „Überschrift: Ja“, Spalte 1, Aufsteigend.",
        check: async () => {
          const tabellen = await readTables();
          const kandidaten = tabellen.filter((tabelle) => tabelle.values.length >= 4);
          if (kandidaten.length === 0) {
            return fail(
              "Es gibt noch keine Tabelle mit Überschrift und mindestens drei Datenzeilen.",
              "Leg eine kleine Tabelle an, etwa Namen mit Abteilung.",
            );
          }
          const sortiert = kandidaten.filter((tabelle) => {
            const werte = tabelle.values.slice(1).map((zeile) => zeile[0] ?? "");
            return werte.every(
              (wert, i) => i === 0 || werte[i - 1].localeCompare(wert, "de", { numeric: true, sensitivity: "base" }) <= 0,
            );
          });
          return fromCriteria(
            [criterion("Eine Tabelle ist nach der ersten Spalte aufsteigend sortiert", sortiert.length > 0)],
            {
              success: "Sortiert – und die Überschriftenzeile ist oben geblieben.",
              failure: "Die Datenzeilen stehen noch nicht in aufsteigender Reihenfolge.",
              hint: "Ist die Überschrift mit einsortiert worden, im Dialog „Überschrift: Ja“ wählen.",
            },
          );
        },
      },
    },
  ],
};
